import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BlogImage } from "./components/blog-image";
import { BlogMeta } from "./components/blog-meta";
import { CategoryBadge } from "./components/category-badge";
import { ReadingProgressBar } from "./components/reading-progress-bar";
import { SharePopup } from "./components/share-popup";
import { BlogPostApi } from "./services/BlogPostApi";

const BlogPost = () => {
  const blogPostApi = new BlogPostApi();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBlogPost = async (slug) => {
    try {
      const response = await blogPostApi.getBySlug(slug);
      setPost(response.post);
    } catch (error) {
      console.error("Failed to fetch blog post:", error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      fetchBlogPost(slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const formattedDate = post?.createdAt
    ? new Date(post.createdAt).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  if (loading) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="container mx-auto px-4 py-6 max-w-7xl flex items-center justify-center">
            <div className="animate-spin h-10 w-10 border-t-2 border-b-2 border-primary rounded-full"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="container mx-auto px-4 py-6 max-w-4xl">
            <div className="text-center py-16">
              <h1 className="text-3xl font-bold mb-4 text-gray-900">
                Blog Post Not Found
              </h1>
              <p className="mb-6 text-gray-600">
                The blog post you're looking for doesn't exist or has been
                removed.
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center text-primary hover:underline"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to all posts
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <main className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4 py-8 md:py-12 max-w-4xl">
          <article className="bg-white rounded-3xl shadow-lg border border-gray-200/60 p-8 md:p-12">
            <ReadingProgressBar />

            {/* Back Navigation */}
            <div className="mb-8">
              <Link
                to="/blog"
                className="inline-flex items-center text-gray-600 hover:text-primary transition-colors duration-200 font-medium group"
              >
                <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                Back to all posts
              </Link>
            </div>

            {/* Category Badge */}
            <div className="mb-5">
              <CategoryBadge category={post.category} />
            </div>

            {/* Title */}
            <h1 className="font-bold text-3xl md:text-4xl lg:text-5xl mb-7 text-gray-900 leading-tight tracking-tight">
              {post.title}
            </h1>

            {/* Meta Information */}
            <BlogMeta
              date={formattedDate}
              author={post.author?.authorName}
              readTime={post.readTime}
              isByOyeCreator={post.isByOyeCreator}
              className="mb-10"
            />

            {/* Featured Image */}
            <BlogImage
              src={post.featuredImage}
              alt={post.title}
              containerClassName="w-full h-[300px] md:h-[500px] mb-12 rounded-2xl shadow-md overflow-hidden"
            />

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div
                className="[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2
            [&_ul_ul]:list-circle [&_ul_ul]:pl-6
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2
            [&_p]:mb-6 [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:text-base
            [&_li]:mb-2 [&_li]:text-gray-700
            [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-8 [&_h1]:text-gray-900 [&_h1]:leading-tight
            [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h2]:mt-6 [&_h2]:text-gray-900 [&_h2]:leading-tight
            [&_h3]:text-xl [&_h3]:font-medium [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-gray-900
            [&_blockquote]:border-l-4 [&_blockquote]:border-primary/30 [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-6 [&_blockquote]:bg-gray-50 [&_blockquote]:py-4 [&_blockquote]:rounded-r-lg
            [&_code]:bg-gray-100 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-pink-600 [&_code]:text-sm [&_code]:font-mono
            [&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-6 [&_pre]:shadow-inner
            [&_hr]:my-8 [&_hr]:border-gray-200
            [&_a]:text-primary [&_a]:hover:underline [&_a]:font-medium
            [&_img]:rounded-lg [&_img]:shadow-md [&_img]:my-6 [&_img]:max-w-full"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            {/* Share Section */}
            <div className="mt-14 flex flex-wrap items-center justify-between gap-4 py-8 border-t border-b border-gray-200 bg-gray-50/50 rounded-lg px-6 -mx-6">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-semibold text-gray-700">
                  Share this article:
                </span>
                <SharePopup url={window.location.href} title={post.title} />
              </div>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
};

export default BlogPost;
